import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, AlertTriangle, CheckCircle2, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const toText = (items = []) => items.map((l) => `${l.item} x ${l.qty_per_group ?? 1}${l.units ? ` ${l.units}` : ""}`).join("\n");
const NONE = "__none";

function Picker({ onFile }) {
  const ref = useRef(null);
  useEffect(() => {
    const onPaste = (e) => {
      const f = [...(e.clipboardData?.files || [])].find((x) => x.type.startsWith("image/"));
      if (f) onFile(f);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [onFile]);
  return (
    <div>
      <input ref={ref} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} data-testid="shot-file-input" />
      <button onClick={() => ref.current?.click()} className="w-full rounded-xl border-2 border-dashed p-8 text-sm text-muted-foreground hover:border-primary/60 transition-colors flex flex-col items-center gap-2" data-testid="shot-file-button">
        <Camera className="h-6 w-6 text-primary" />
        <span className="text-foreground font-medium">Choose a screenshot or photo of the experiment</span>
        <span className="text-xs">A worksheet, method sheet, textbook page or the apparatus itself. You can also paste an image (Ctrl/Cmd + V).</span>
      </button>
    </div>
  );
}

function PreviewForm({ f, setF, staff }) {
  const set = (k) => (e) => setF((o) => ({
    ...o,
    [k]: e.target ? e.target.value : e,
    _incomplete: !(
      String(k === "name" ? e.target.value : o.name || "").trim()
      && (
        String(k === "equipment" ? e.target.value : o.equipment || "").trim()
        || String(k === "chemicals" ? e.target.value : o.chemicals || "").trim()
      )
    ),
  }));
  return (
    <div className="space-y-3" data-testid="shot-preview-form">
      <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={f.name} onChange={set("name")} data-testid="shot-field-name" /></div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="space-y-1"><Label className="text-xs">Subject</Label>
          <Select value={f.subject || NONE} onValueChange={(v) => setF((o) => ({ ...o, subject: v === NONE ? "" : v }))}>
            <SelectTrigger data-testid="shot-field-subject"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={NONE}>Not set</SelectItem>{["Biology", "Chemistry", "Physics"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1"><Label className="text-xs">Year groups</Label><Input value={f.year_groups} onChange={set("year_groups")} placeholder="e.g. 8, 9" data-testid="shot-field-years" /></div>
        <div className="space-y-1"><Label className="text-xs">Topic</Label><Input value={f.topic} onChange={set("topic")} data-testid="shot-field-topic" /></div>
      </div>
      <div className="space-y-1"><Label className="text-xs">Equipment per group (one per line: item x qty)</Label><Textarea rows={6} value={f.equipment} onChange={set("equipment")} className="font-mono text-xs" data-testid="shot-field-equipment" /></div>
      <div className="space-y-1"><Label className="text-xs">Chemicals & materials</Label><Textarea rows={3} value={f.chemicals} onChange={set("chemicals")} className="font-mono text-xs" data-testid="shot-field-chemicals" /></div>
      <div className="space-y-1"><Label className="text-xs">Method summary</Label><Textarea rows={2} value={f.description} onChange={set("description")} data-testid="shot-field-description" /></div>
      <div className="space-y-1"><Label className="text-xs">Technician notes</Label><Textarea rows={2} value={f.technician_notes} onChange={set("technician_notes")} data-testid="shot-field-tech-notes" /></div>
      {staff && (
        <div className="space-y-1 max-w-xs"><Label className="text-xs">Review status</Label>
          <Select value={f.review_status} onValueChange={(v) => setF((o) => ({ ...o, review_status: v }))}>
            <SelectTrigger data-testid="shot-field-review"><SelectValue /></SelectTrigger>
            <SelectContent>{["Draft", "Under review", "Approved"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select></div>
      )}
    </div>
  );
}

export function ScreenshotPracticalDialog({ open, onOpenChange, onSaved }) {
  const { can } = useAuth();
  const staff = can("practical:write");
  const [img, setImg] = useState(null);
  const [f, setF] = useState(null);
  const [uncertain, setUncertain] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setImg(null); setF(null); setUncertain([]); }
  }, [open]);
  const read = async (file) => {
    setImg(URL.createObjectURL(file));
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/practicals/from-image/read", fd);
      setF({ ...data, year_groups: data.year_groups.join(", "), equipment: toText(data.equipment), chemicals: toText(data.chemicals), review_status: "Draft" });
      setUncertain(data.uncertain || []);
    } catch (e) {
      toast.error(errMsg(e));
      setImg(null);
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/practicals/from-image/save", f);
      toast.success(`${data.name} added to the practical library`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="shot-dialog">
        <DialogHeader>
          <DialogTitle>Practical from a screenshot</DialogTitle>
          <DialogDescription>Jonny reads the image and fills in the title and equipment. Check everything before saving.{!staff && " It's saved as a Draft that you can book straight away; a technician will review it."}</DialogDescription>
        </DialogHeader>
        {!img && <Picker onFile={read} />}
        {img && (
          <div className="grid sm:grid-cols-[160px_1fr] gap-4">
            <img src={img} alt="Uploaded experiment" className="rounded-lg border w-full max-h-56 object-contain bg-muted" data-testid="shot-image-preview" />
            {busy && !f ? <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="shot-reading"><Loader2 className="h-4 w-4 animate-spin" />Jonny is reading the screenshot…</div> : f && (
              <div className="space-y-3">
                {f._incomplete && (
                  <div
                    className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900"
                    data-testid="shot-incomplete-notice"
                  >
                    The screenshot is missing a title or apparatus details. Complete them before saving.
                  </div>
                )}
                {uncertain.length > 0 && <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2.5 text-xs space-y-1" data-testid="shot-uncertain">{uncertain.map((u, i) => <p key={i} className="flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />{u}</p>)}</div>}
                <PreviewForm f={f} setF={setF} staff={staff} />
                <p className="text-xs text-muted-foreground" data-testid="shot-attach-note">The original image will be attached to this practical as a worksheet, so technicians can see the source.</p>
              </div>
            )}
          </div>
        )}
        <DialogFooter className="gap-2">
          {img && !busy && <Button variant="ghost" onClick={() => { setImg(null); setF(null); }} data-testid="shot-retry-button">Use another image</Button>}
          <Button onClick={save} disabled={!f || busy || !f.name.trim() || f._incomplete} data-testid="shot-save-button">{busy && f ? "Saving…" : "Save to library"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PracticalCreatedCard({ practical, onBook, disabled }) {
  return (
    <div className="rounded-xl border border-emerald-500/40 bg-emerald-50/60 dark:bg-emerald-950/20 p-4 space-y-2" data-testid="practical-created-card">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />Added to the practical library · {practical.review_status}</div>
      <div className="font-semibold" data-testid="practical-created-name">{practical.name}</div>
      <p className="text-xs text-muted-foreground">{(practical.equipment || []).length} equipment items{practical.chemicals?.length ? `, ${practical.chemicals.length} materials` : ""}</p>
      <Button size="sm" variant="outline" disabled={disabled} onClick={() => onBook(`Book "${practical.name}" for my next lesson`)} data-testid="practical-created-book-button"><CalendarPlus className="h-3.5 w-3.5 mr-1.5" />Book it for my next lesson</Button>
    </div>
  );
}
